import clearForm from "@iewc/Store/Handlers/RfqRequestQuote/ClearForm";
import submitRequestQuoteForm from "@iewc/Store/Handlers/RfqRequestQuote/SubmitRequestQuoteForm";
import loadCustomCountries from "@iewc/Store/Handlers/Utilities/CustomLoadCountries";
import { setGA4Event } from "@iewc/Store/Handlers/Utilities/CustomTracking";
import { CustomState } from "@iewc/Store/Reducers";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import resetReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ResetReCaptcha";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
    };
}

const mapDispatchToProps = {
    submitRequestQuoteForm,
    clearForm,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
    loadCustomCountries,
    resetReCaptcha,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequestQuoteFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    captchaWrapper?: InjectableCss;
    captchaLabel?: TypographyPresentationProps;
    continueButton?: ButtonPresentationProps;
}

const mapStateToProps = (state: CustomState) => {
    return {
        countries: state.customCountriesList.countries as CountryModel[],
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
        billTo: getBillToState(state, state.context?.session?.billToId).value,
    };
};

export const requestQuoteFormStyles: RequestQuoteFormStyles = {
    container: {
        gap: 0,
        css: css`
            padding-top: 30px;
            padding-bottom: 30px;

            /* stylelint-disable-next-line */
            #requestQuoteForm {
                width: 100%;
            }
        `,
    },
    formGridItem: { width: 12 },
    submitButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    messageTextGridItem: { width: 12 },
    captchaWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
    captchaLabel: {
        variant: "body",
        forwardAs: "label",
        weight: "bold",
        css: css`
            display: block;
            margin-bottom: 6px;
        `,
    },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
};

const styles = requestQuoteFormStyles;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
    countries: any[];
};

export const RequestQuoteFormContext = React.createContext<ContextType>({
    validators: {},
    countries: [],
});

const RequestQuoteForm = ({
    id,
    fields: widgetFields,
    submitRequestQuoteForm: widgetSubmitRequestQuoteForm,
    clearForm: widgetClearForm,
    validateReCaptcha: widgetValidateReCaptcha,
    countries,
    loadCustomCountries,
    resetReCaptcha,
    isAuthenticated,
    billTo,
}: Props) => {
    const { emailRecipients, submitButtonText, successMessage } = widgetFields;
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = await widgetValidateReCaptcha({ location: "ContactUs", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        widgetSubmitRequestQuoteForm({
            emailRecipients,
            onSuccess: () => {
                let sapAccountNumber = "";
                if (isAuthenticated) {
                    sapAccountNumber = billTo ? billTo.customerNumber.replace(/^0+/, "") : "__IS_LOADING__";
                }
                const formID = "requestQuoteForm";
                const accountNumber = sapAccountNumber;
                const formType = "Request a Quote";
                setGA4Event([], "formSubmissionSuccess", { formType, formID, accountNumber });

                setIsModalOpen(true);
                setIsSubmitting(false);
                if (recaptchaRef.current) {
                    resetReCaptcha({ location: "ContactUs" });
                }
            },
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    const modalCloseHandler = () => {
        successHandler();
        setIsModalOpen(false);
    };

    const successHandler = () => {
        setIsModalOpen(false);
        widgetClearForm();
    };

    useEffect(() => {
        loadCustomCountries({});
    }, []);

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <RequestQuoteFormContext.Provider value={{ validators, countries } as any}>
                    <form id="requestQuoteForm" onSubmit={handleFormSubmit} noValidate>
                        <Zone contentId={id} zoneName="Content" />
                        <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                        <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                            {submitButtonText}
                        </Button>
                        <Modal
                            headline={translate("Request a quote")}
                            contentLabel={successMessage}
                            isOpen={isModalOpen}
                            handleClose={modalCloseHandler}
                        >
                            <GridContainer>
                                <GridItem {...styles.messageTextGridItem}>
                                    <Typography {...styles.messageText}>{successMessage}</Typography>
                                </GridItem>
                                <GridItem {...styles.buttonGridItem}>
                                    <Button {...styles.continueButton} onClick={() => successHandler()}>
                                        {translate("Continue")}
                                    </Button>
                                </GridItem>
                            </GridContainer>
                        </Modal>
                    </form>
                </RequestQuoteFormContext.Provider>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestQuoteForm),
    definition: {
        group: "Request a Quote Form" as any,
        icon: "FormField",
        displayName: "Request a Quote Form",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: emailsRegexp,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit Request",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
